import { cloneElement, memo, useState, useEffect } from 'react';
import { DatagridBodyProps, DatagridRowProps, Identifier } from 'react-admin';
import { TableBody } from '@material-ui/core';
import { useDrag, useDrop } from 'react-dnd';
import { arrayMoveImmutable } from 'array-move';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';

import DatagridRow from './DatagridRow';
import { app } from '../../App';

type SortableDatagridBodyRowProps = DatagridRowProps & {
  filterColumn?: string;
  moveRecords?: (dragIndex: number, hoverIndex: number) => void;
  rowIndex: number;
  toggleLoading?: (active: boolean) => void;
};

type SortableDatagridBodyProps = DatagridBodyProps & {
  filterColumn?: string;
};

interface DraggableRowProps {
  hoverId?: Identifier;
  id: Identifier;
  type: string;
  filterColumn?: string;
  rowIndex: number;
}

const Row = ({
  id,
  rowIndex,
  moveRecords,
  filterColumn,
  toggleLoading,
  ...props
}: SortableDatagridBodyRowProps) => {
  const type = filterColumn ? `ROW-${props.record?.[filterColumn]}` : 'ROW';
  const [{ isDragging }, dragRef] = useDrag<
    DraggableRowProps,
    any,
    { isDragging: boolean }
  >({
    item: { 
      type,
      id: id || '',
      rowIndex,
      filterColumn: filterColumn ? props.record?.[filterColumn] : undefined
    },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    end(item, monitor) {
      if (monitor.didDrop()) {
        let params: any = {
          resource: props.resource,
          fromId: item.id,
          toId: item.hoverId
        };

        if (filterColumn)
          params = {
            ...params,
            conditions: { [filterColumn]: item.filterColumn }
          };

        toggleLoading?.(true);
        app.service('sort')
          .create(params)
          .then(() => toggleLoading?.(false))
          .catch(() => toggleLoading?.(false));
      }
    }
  });

  const [, dropRef] = useDrop<DraggableRowProps, any, any>({
    accept: type,
    hover(item, monitor) {
      const { rowIndex: dragIndex } = item;
      const hoverIndex = rowIndex;
      const hoverId = id;

      if (dragIndex === hoverIndex) return;

      moveRecords?.(dragIndex, hoverIndex);
      item.rowIndex = hoverIndex;
      item.hoverId = hoverId;
    }
  });

  return (
    <DatagridRow
      ref={node => dragRef(dropRef(node))}
      {...{ ...props, id }}
      isDragging={isDragging}
    />
  );
};

function SortableDatagridBody({
  basePath,
  children,
  classes,
  className,
  data = {},
  expand,
  hasBulkActions = false,
  hover,
  ids = [],
  onToggleItem,
  resource,
  row = <Row rowIndex={0} />,
  rowClick,
  rowStyle,
  selectedIds,
  isRowSelectable,
  filterColumn,
  ...rest
}: SortableDatagridBodyProps) {
  const defaultRowProps = {
    basePath,
    classes,
    expand,
    hasBulkActions,
    hover,
    onToggleItem,
    resource,
    rowClick
  };

  const [records, setRecords] = useState<Identifier[]>([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setRecords(ids);
  }, [ids]);

  const moveRecords = async (
    draggedRowIndex: number,
    droppedRowIndex: number,
    draggedId?: number,
    droppedId?: number
  ) => {
    setRecords(arrayMoveImmutable(records, draggedRowIndex, droppedRowIndex));
  };

  function toggleLoading(loading: boolean) {
    setLoading(loading);
  }

  return (
    <TableBody
      className={classnames('datagrid-body', className)}
      style={{ pointerEvents: loading ? 'none' : 'unset' }}
      {...rest}
    >
      {records.map((id, rowIndex) =>
        cloneElement(
          row,
          {
            ...defaultRowProps,
            filterColumn,
            rowIndex,
            className: classes
              ? classnames(classes.row, {
                  [classes.rowEven]: rowIndex % 2 === 0,
                  [classes.rowOdd]: rowIndex % 2 !== 0,
                  [classes.clickableRow]: rowClick
                })
              : undefined,
            record: data[id],
            moveRecords,
            toggleLoading,
            id,
            key: id,
            selectable: !isRowSelectable || isRowSelectable(data[id]),
            selected: selectedIds?.includes(id),
            style: rowStyle ? rowStyle(data[id], rowIndex) : null
          },
          children
        )
      )}
    </TableBody>
  );
};

SortableDatagridBody.muiName = 'TableBody';

const areEqual = (prevProps, nextProps) => {
  const { children: _, ...prevPropsWithoutChildren } = prevProps;
  const { children: __, ...nextPropsWithoutChildren } = nextProps;
  return isEqual(prevPropsWithoutChildren, nextPropsWithoutChildren);
};

export const PureSortableDatagridBody = memo(SortableDatagridBody, areEqual);

export default SortableDatagridBody;
